<template>
  <section :class="['chat-panel', `chat-panel--${backgroundState}`]">
    <div v-if="!selectedDialog" class="chat-panel__empty">
      <div class="chat-panel__empty-card">
        <img src="/img/chat.svg" alt="" class="chat-panel__empty-image" />
        <div class="chat-panel__empty-title">Диалог не выбран</div>
        <p>Выберите или создайте диалог для начала общения</p>
      </div>
    </div>

    <template v-else>
      <header class="chat-panel__header">
        <div class="chat-panel__header-main">
          <a
            v-if="selectedDialog.client_phone"
            :href="`tel:${selectedDialog.client_phone}`"
            class="chat-panel__header-icon"
            aria-label="Позвонить клиенту"
          >
            <img src="/img/dialogs/header-phone.png" alt="" class="chat-panel__header-icon-image" />
          </a>
          <span v-else class="chat-panel__header-icon chat-panel__header-icon--static">
            <img src="/img/dialogs/header-phone.png" alt="" class="chat-panel__header-icon-image" />
          </span>

          <div class="chat-panel__header-meta">
            <div class="chat-panel__title">{{ selectedDialog.client_name || 'Диалог' }}</div>
            <div class="chat-panel__subtitle">{{ headerStatusText }}</div>
          </div>
        </div>

        <button type="button" class="chat-panel__header-action" aria-label="Редактировать диалог">
          <img src="/img/dialogs/edit-chat.png" alt="" class="chat-panel__header-action-image" />
        </button>
      </header>

      <div class="chat-panel__messages message-block" @scroll.passive="handleScroll" ref="chatContainer">
        <div v-for="(group, index) in groupedMessages" :key="index" class="chat-group">
          <div v-if="group.type === 'date'" class="chat-date-chip">{{ group.date }}</div>

          <div
            v-else
            class="chat-message-row"
            :class="group.isMine ? 'chat-message-row--mine' : 'chat-message-row--other'"
          >
            <div
              class="chat-message-stack"
              :class="group.isMine ? 'chat-message-stack--mine' : 'chat-message-stack--other'"
            >
              <div
                v-for="message in group.messages"
                :key="message.id"
                :data-message-id="message.id"
                :class="[
                  'chat-message-item',
                  { 'chat-message-item--voice': isVoiceMessage(message) },
                  { 'chat-message-item--call': isCallMessage(message) }
                ]"
              >
                <template v-if="isCallMessage(message)">
                  <div class="chat-call-card">
                    <img :src="callDirectionIcon(message)" alt="" class="chat-call-card__arrow" />
                    <img src="/img/dialogs/header-phone.png" alt="" class="chat-call-card__phone" />
                    <div class="chat-call-card__meta">
                      <div class="chat-call-card__primary">{{ callPrimaryText(message) }}</div>
                      <div v-if="callSecondaryText(message)" class="chat-call-card__secondary">
                        {{ callSecondaryText(message) }}
                      </div>
                    </div>
                  </div>
                </template>

                <template v-else-if="isVoiceMessage(message)">
                  <div class="chat-voice-card">
                    <button type="button" class="chat-voice-card__play" aria-label="Воспроизвести голосовое сообщение">
                      <span class="chat-voice-card__triangle"></span>
                    </button>
                    <div
                      class="chat-voice-card__wave"
                      :style="{ backgroundImage: `url(${voiceWaveSrc})` }"
                      aria-hidden="true"
                    ></div>
                    <div class="chat-voice-card__duration">{{ voiceDurationText(message) }}</div>
                  </div>
                </template>

                <template v-else-if="shouldShowContent(message)">
                  <div
                    v-if="isHtmlAllowed()"
                    v-html="message.content"
                    class="chat-message-text"
                  ></div>
                  <div v-else class="chat-message-text">{{ message.content }}</div>
                </template>

                <div v-if="fileUrl(message) && !isVoiceMessage(message) && !isCallMessage(message)" class="chat-attachments">
                  <template v-if="isImageUrl(fileUrl(message))">
                    <el-image
                      :src="fileUrl(message)"
                      :preview-src-list="[fileUrl(message)]"
                      fit="cover"
                      class="chat-attachment-image"
                    />
                    <a :href="fileUrl(message)" :download="attachmentName(message)" class="chat-attachment-link">
                      <el-icon><Download /></el-icon>
                      <span>Скачать</span>
                    </a>
                  </template>

                  <template v-else-if="isVideoUrl(fileUrl(message), message)">
                    <video :src="fileUrl(message)" controls class="chat-attachment-video" />
                    <a :href="fileUrl(message)" :download="attachmentName(message)" class="chat-attachment-link">
                      <el-icon><Download /></el-icon>
                      <span>Скачать</span>
                    </a>
                  </template>

                  <template v-else-if="isAudioUrl(fileUrl(message), message)">
                    <audio :src="fileUrl(message)" controls class="chat-attachment-audio" />
                    <a :href="fileUrl(message)" :download="attachmentName(message)" class="chat-attachment-link">
                      <el-icon><Download /></el-icon>
                      <span>Скачать</span>
                    </a>
                  </template>

                  <template v-else>
                    <a :href="fileUrl(message)" :download="attachmentName(message)" class="chat-attachment-link">
                      <el-icon><Download /></el-icon>
                      <span>{{ attachmentName(message) }}</span>
                    </a>
                  </template>
                </div>

                <div class="chat-message-meta">{{ formatBubbleTime(message.created_at) }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <footer class="chat-panel__composer">
        <el-upload
          ref="uploadRef"
          class="chat-panel__upload"
          :auto-upload="false"
          :action="uploadUrl"
          :data="{ client_id: selectedDialog?.client_id }"
          :show-file-list="false"
          :on-remove="handleUploadRemove"
          :before-upload="beforeUpload"
          :on-change="handleFileChange"
          :headers="{ Authorization: 'Bearer ' + getToken() }"
          v-model:file-list="uploadFiles"
        />

        <div v-if="uploadFiles.length" class="chat-panel__files">
          <div v-for="file in uploadFiles" :key="file.uid" class="chat-panel__file">
            <span class="chat-panel__file-name">{{ file.name }}</span>
            <el-progress
              v-if="typeof file.percentage === 'number'"
              :percentage="Math.round(file.percentage)"
              :stroke-width="6"
              :status="file.status === 'success' ? 'success' : (file.status === 'fail' ? 'exception' : undefined)"
              class="chat-panel__file-progress"
            />
            <span v-else class="chat-panel__file-ready">Готов к отправке</span>
            <button type="button" class="chat-panel__file-remove" @click="removeFile(file)">
              <el-icon><Close /></el-icon>
            </button>
          </div>
        </div>

        <div class="chat-panel__composer-shell">
          <QuillEditor
            ref="editor"
            v-model:content="messageHtml"
            contentType="html"
            theme="snow"
            :toolbar="[]"
            placeholder="Сообщение..."
            class="chat-editor"
            @keydown.enter.exact.prevent="sendMessage"
          />

          <div class="chat-panel__composer-actions">
            <button type="button" class="chat-panel__icon-btn" @click="triggerFileSelect">
              <img src="/img/dialogs/attach.png" alt="" class="chat-panel__composer-icon-image" />
            </button>
            <button
              type="button"
              class="chat-panel__icon-btn chat-panel__icon-btn--primary"
              @click="sendMessage"
              :disabled="!canSendMessage"
            >
              <img src="/img/dialogs/mic.png" alt="" class="chat-panel__composer-icon-image" />
            </button>
          </div>
        </div>
      </footer>
    </template>
  </section>
</template>

<script>
import axios from 'axios'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { mapState } from 'vuex'
import { QuillEditor } from '@vueup/vue-quill'
import '@vueup/vue-quill/dist/vue-quill.snow.css'
import { Close, Download } from '@element-plus/icons-vue'

dayjs.extend(utc)
dayjs.extend(timezone)

export default {
  name: 'ChatPanel',
  components: {
    Close,
    Download,
    QuillEditor
  },
  props: {
    selectedDialog: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      loadingOlder: false,
      messageHtml: '',
      page: 1,
      pageSize: 25,
      seenFlushTimeout: null,
      observerInitialized: false,
      uploadUrl: this.$store.getters['messengers/getUploadUrl'],
      uploadFiles: [],
      pendingFiles: [],
      isSending: false,
      sendFileUrl: 'https://lk.lineon.me/api/messages.sendFile',
      voiceWaveSrc: '/img/dialogs/voice-wave.png'
    }
  },
  computed: {
    ...mapState('auth', ['user']),
    ...mapState('messengers', ['messages', 'messengers']),
    ...mapState('socket', ['waAuthCheckInProgress', 'whatsappReadyStatuses']),
    currentMessenger() {
      return this.messengers.find(messenger => Number(messenger.id) === Number(this.selectedDialog?.messenger_id)) || null
    },
    headerStatusText() {
      const rawDate = this.selectedDialog?.last_active
      if (!rawDate) return 'был(а) недавно'

      const timezoneName = this.user?.timezone || 'Europe/Moscow'
      const parsed = dayjs(String(rawDate).replace(' ', 'T')).tz(timezoneName)
      if (!parsed.isValid()) return 'был(а) недавно'

      const now = dayjs().tz(timezoneName)
      const minutesDiff = Math.abs(now.diff(parsed, 'minute'))
      if (minutesDiff <= 30) return 'был(а) недавно'
      if (parsed.isSame(now, 'day')) return `в сети ${parsed.format('HH:mm')}`
      if (parsed.isSame(now.subtract(1, 'day'), 'day')) return 'был(а) вчера'
      return `был(а) ${parsed.format('DD.MM.YYYY')}`
    },
    backgroundState() {
      const raw = String(
        this.selectedDialog?.chat_background_state ||
        this.selectedDialog?.background_state ||
        this.selectedDialog?.ui_state ||
        this.selectedDialog?.dialog_state ||
        this.selectedDialog?.booking_state ||
        this.selectedDialog?.status ||
        ''
      ).toLowerCase()

      if (['success', 'green', 'confirmed', 'paid', 'completed'].includes(raw)) return 'success'
      if (['overdue', 'expired', 'red', 'late', 'canceled', 'cancelled'].includes(raw)) return 'overdue'
      return 'default'
    },
    canSendMessage() {
      return !this.isSending && (this.hasMeaningfulHtml(this.messageHtml) || this.pendingFiles.length > 0)
    },
    sortedMessages() {
      const dialogId = this.selectedDialog?.id
      if (!dialogId) return []
      return this.messages
        .filter(message => String(message.dialog_id) === String(dialogId))
        .map(message => ({
          ...message,
          id: Number(message.id),
          dialog_id: Number(message.dialog_id),
          sender_user_id: message.sender_user_id ? Number(message.sender_user_id) : null,
          created_at: new Date(Number(message.created_at) * 1000),
          updated_at: message.updated_at ? new Date(Number(message.updated_at) * 1000) : null,
          attachments: message.attachments
            ? (typeof message.attachments === 'string'
              ? (() => {
                try {
                  return JSON.parse(message.attachments)
                } catch {
                  return null
                }
              })()
              : message.attachments)
            : null,
          sender_name: message.sender_name || ''
        }))
        .sort((first, second) => first.created_at - second.created_at)
    },
    groupedMessages() {
      const groups = []
      let currentGroup = null
      let lastDate = null

      for (const message of this.sortedMessages) {
        const messageDate = dayjs(message.created_at).format('DD.MM.YYYY')

        if (messageDate !== lastDate) {
          groups.push({ type: 'date', date: messageDate })
          lastDate = messageDate
        }

        const timestamp = message.created_at.getTime()
        if (
          currentGroup &&
          currentGroup.type === 'messages' &&
          currentGroup.sender_type === message.sender_type &&
          currentGroup.sender_user_id === message.sender_user_id &&
          Math.abs(timestamp - currentGroup.lastTimestamp) <= 60000
        ) {
          currentGroup.messages.push(message)
          currentGroup.lastTimestamp = timestamp
        } else {
          if (currentGroup) groups.push(currentGroup)
          currentGroup = {
            type: 'messages',
            sender_type: message.sender_type,
            sender_user_id: message.sender_user_id,
            isMine: message.sender_type === 'manager',
            messages: [message],
            lastTimestamp: timestamp
          }
        }
      }

      if (currentGroup) groups.push(currentGroup)
      return groups
    }
  },
  watch: {
    selectedDialog: {
      immediate: true,
      handler(newVal, oldVal) {
        try {
          const token = this.$cookies.get('token') || (this.$store.state && this.$store.state.cookies && this.$store.state.cookies.get && this.$store.state.cookies.get('token'))
          if (token) {
            const decodeJwt = (typeof window !== 'undefined' && window.decodeJwt) ? window.decodeJwt : (require && require('jwt-decode'))
            const decoded = typeof decodeJwt === 'function' ? decodeJwt(token) : decodeJwt.default(token)
            const accountId = decoded.account_id
            if (oldVal && oldVal.id) {
              const oldChannel = `new-message_${accountId}_${oldVal.id}`
              if (this.$store.state.socket && this.$store.state.socket.socket && this.$store.state.socket.socket.off) {
                this.$store.state.socket.socket.off(oldChannel)
              }
            }
          }
        } catch (error) {
          console.error(error)
        }

        this.page = 1
        this.observerInitialized = false
        this.pendingFiles = []
        this.uploadFiles = []

        if (!newVal) {
          this.messageHtml = ''
        }

        this.$nextTick(() => {
          this.scrollToBottom()
        })
      }
    },
    messages() {
      if (!this.loadingOlder) {
        this.scrollToBottom()
      }
    },
    sortedMessages(newValue) {
      if (newValue.length && this.observer) {
        this.observer.disconnect()
      }

      if (newValue.length && !this.observerInitialized) {
        this.observerInitialized = true
        this.$nextTick(() => {
          const container = this.$refs.chatContainer
          if (!container || !this.observer) return
          const allElements = container.querySelectorAll('[data-message-id]')
          allElements.forEach(element => {
            this.observer.observe(element)
          })
        })
      }
    }
  },
  methods: {
    triggerFileSelect() {
      const upload = this.$refs.uploadRef
      if (upload && upload.$el) {
        const input = upload.$el.querySelector('input[type=file]')
        if (input) input.click()
      }
    },
    getToken() {
      return document.cookie.split('; ').find(row => row.startsWith('token='))?.split('=')[1] || ''
    },
    handleUploadRemove(file, fileList) {
      this.uploadFiles = fileList
      this.pendingFiles = fileList.map(item => item)
    },
    removeFile(file) {
      this.uploadFiles = this.uploadFiles.filter(item => item.uid !== file.uid)
      this.pendingFiles = this.pendingFiles.filter(item => item.uid !== file.uid)
    },
    formatDate(date) {
      if (!date) return ''
      const accountTimezone = this.user.timezone || 'Europe/Moscow'
      const local = dayjs(date).tz(accountTimezone)
      const now = dayjs().tz(accountTimezone)
      const yesterday = now.subtract(1, 'day')

      if (local.isSame(now, 'day')) {
        return `Сегодня ${local.format('HH:mm')}`
      }
      if (local.isSame(yesterday, 'day')) {
        return `Вчера ${local.format('HH:mm')}`
      }
      return local.isValid() ? local.format('DD.MM.YYYY HH:mm') : ''
    },
    formatBubbleTime(date) {
      if (!date) return ''
      const accountTimezone = this.user.timezone || 'Europe/Moscow'
      const local = dayjs(date).tz(accountTimezone)
      return local.isValid() ? local.format('HH:mm') : ''
    },
    messageTypeValue(message) {
      return [
        message?.message_type,
        message?.type,
        message?.event_type,
        message?.kind,
        message?.subtype,
        message?.call_type,
        message?.call_status
      ]
        .filter(Boolean)
        .map(value => String(value).toLowerCase())
        .join(' ')
    },
    isVoiceMessage(message) {
      const typeValue = this.messageTypeValue(message)
      if (/(voice|audio|ptt|voice_note|audio_note)/.test(typeValue)) return true
      return this.isAudioUrl(this.fileUrl(message), message)
    },
    isCallMessage(message) {
      const typeValue = this.messageTypeValue(message)
      if (/(call|missed|declined|rejected|incoming|outgoing)/.test(typeValue)) return true
      return Boolean(message?.call_status || message?.call_duration || message?.call_direction)
    },
    voiceDurationText(message) {
      const raw =
        message?.voice_duration ||
        message?.duration ||
        message?.audio_duration ||
        message?.meta?.duration

      if (raw == null || raw === '') return this.formatBubbleTime(message.created_at)

      const seconds = Number(raw)
      if (Number.isNaN(seconds)) return String(raw)
      const mins = Math.floor(seconds / 60)
      const secs = seconds % 60
      return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
    },
    callDirectionIcon(message) {
      const typeValue = this.messageTypeValue(message)
      if (/(missed|declined|rejected|failed|cancelled|canceled)/.test(typeValue)) {
        return '/img/dialogs/call-arrow-down-red.png'
      }
      if (/(outgoing|dialed|dialled)/.test(typeValue)) {
        return '/img/dialogs/call-arrow-down-blue.png'
      }
      return '/img/dialogs/call-arrow-up.png'
    },
    callPrimaryText(message) {
      const duration = message?.call_duration || message?.duration || message?.meta?.duration
      if (duration != null && duration !== '') {
        const seconds = Number(duration)
        if (!Number.isNaN(seconds)) {
          const mins = Math.floor(seconds / 60)
          const secs = seconds % 60
          return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
        }
        return String(duration)
      }
      if (message?.content) return String(message.content).replace(/<[^>]+>/g, '').trim() || 'Звонок'
      return 'Звонок'
    },
    callSecondaryText(message) {
      if (message?.call_status_text) return String(message.call_status_text)
      return this.formatBubbleTime(message.created_at)
    },
    hasMeaningfulHtml(html) {
      return String(html || '')
        .replace(/<p><br><\/p>/gi, '')
        .replace(/<p>\s*<\/p>/gi, '')
        .replace(/<br\s*\/?>(?=\s*$)/gi, '')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, ' ')
        .trim().length > 0
    },
    async handleScroll() {
      const container = this.$refs.chatContainer
      if (!container || container.scrollTop !== 0 || this.loadingOlder) return

      const previousHeight = container.scrollHeight
      this.loadingOlder = true
      this.page += 1

      await this.$store.dispatch('messengers/fetchMessages', {
        dialog_id: this.selectedDialog.id,
        page: this.page,
        pageSize: this.pageSize
      })

      this.loadingOlder = false
      this.$nextTick(() => {
        const newHeight = container.scrollHeight
        container.scrollTop = newHeight - previousHeight
      })
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.chatContainer
        if (!container) return
        container.scrollTop = container.scrollHeight
      })
    },
    isHtmlAllowed() {
      return ['email', 'telegram', 'whatsapp'].includes(this.currentMessenger?.type)
    },
    async sendMessage() {
      const html = String(this.messageHtml || '').trim()
      if (!this.hasMeaningfulHtml(html) && this.pendingFiles.length === 0) return

      const messenger = this.currentMessenger
      if (!messenger) return

      if (messenger.type === 'whatsapp') {
        this.isSending = true
        try {
          if (this.pendingFiles.length > 0) {
            const rawHtml = html
            for (let index = 0; index < this.pendingFiles.length; index += 1) {
              const file = this.pendingFiles[index]
              const formData = new FormData()
              formData.append('dialog_id', Number(this.selectedDialog.id))
              formData.append('files[]', file.raw)
              formData.append('caption', index === 0 ? rawHtml : '')
              formData.append('content', rawHtml)
              formData.append('content_raw', rawHtml)
              formData.append('messenger_id', this.selectedDialog.messenger_id)
              formData.append('client_id', Number(this.selectedDialog.client_phone))
              formData.append('client_phone', this.selectedDialog.client_phone)
              formData.append('messenger_type', messenger.type)

              file.status = 'uploading'
              file.percentage = 0
              try {
                await axios.post(this.sendFileUrl, formData, {
                  headers: { Authorization: 'Bearer ' + this.getToken() },
                  onUploadProgress: event => {
                    if (event.total) file.percentage = Math.round((event.loaded / event.total) * 100)
                  }
                })
                file.status = 'success'
              } catch (error) {
                console.error('sendFile error', error)
                file.status = 'fail'
              }
              await new Promise(resolve => setTimeout(resolve, 300))
            }

            this.pendingFiles = []
            this.uploadFiles = []
            this.messageHtml = ''
            if (this.$refs.editor && this.$refs.editor.setHTML) {
              this.$refs.editor.setHTML('')
            }
            this.scrollToBottom()
          } else {
            this.$store.dispatch('socket/send', {
              event: 'send-whatsapp-message',
              data: {
                messenger_id: messenger.id,
                client_id: Number(this.selectedDialog.client_phone),
                dialog_id: Number(this.selectedDialog.id),
                content: html
              }
            })
            this.messageHtml = ''
            if (this.$refs.editor && this.$refs.editor.setHTML) {
              this.$refs.editor.setHTML('')
            }
            this.scrollToBottom()
          }
        } finally {
          this.isSending = false
        }
      } else if (messenger.type === 'email') {
        this.isSending = true
        try {
          const formData = new FormData()
          formData.append('dialog_id', Number(this.selectedDialog.id))
          formData.append('text', html)
          for (const file of this.pendingFiles) {
            formData.append('files[]', file.raw)
          }

          await axios.post('https://lk.lineon.me/api/email.send', formData, {
            headers: { Authorization: 'Bearer ' + this.getToken() }
          })

          this.pendingFiles = []
          this.uploadFiles = []
          this.messageHtml = ''
          if (this.$refs.editor && this.$refs.editor.setHTML) {
            this.$refs.editor.setHTML('')
          }
          this.scrollToBottom()
        } finally {
          this.isSending = false
        }
      } else if (messenger.type === 'telegram') {
        this.isSending = true
        try {
          const text = html
          if (!this.hasMeaningfulHtml(text) && this.pendingFiles.length === 0) return

          if (this.pendingFiles.length > 0) {
            for (let index = 0; index < this.pendingFiles.length; index += 1) {
              const file = this.pendingFiles[index]
              const formData = new FormData()
              formData.append('dialog_id', Number(this.selectedDialog.id))
              formData.append('files[]', file.raw)
              formData.append('messenger_id', this.selectedDialog.messenger_id)
              formData.append('client_id', Number(this.selectedDialog.client_id))
              formData.append('caption', index === 0 ? text : '')
              formData.append('messenger_type', messenger.type)

              file.status = 'uploading'
              file.percentage = 0
              try {
                await axios.post(this.sendFileUrl, formData, {
                  headers: { Authorization: 'Bearer ' + this.getToken() },
                  onUploadProgress: event => {
                    if (event.total) file.percentage = Math.round((event.loaded / event.total) * 100)
                  }
                })
                file.status = 'success'
              } catch (error) {
                console.error('sendFile error', error)
                file.status = 'fail'
              }
              await new Promise(resolve => setTimeout(resolve, 300))
            }
            this.pendingFiles = []
            this.uploadFiles = []
            this.messageHtml = ''
            if (this.$refs.editor && this.$refs.editor.setHTML) {
              this.$refs.editor.setHTML('')
            }
            this.scrollToBottom()
          } else {
            this.$store.dispatch('socket/send', {
              event: 'send-telegram-message',
              data: {
                messenger_id: messenger.id,
                dialog_id: Number(this.selectedDialog.id),
                client_id: Number(this.selectedDialog.client_id),
                content: text
              }
            })
            this.messageHtml = ''
            this.uploadFiles = []
            this.pendingFiles = []
            if (this.$refs.editor && this.$refs.editor.setHTML) {
              this.$refs.editor.setHTML('')
            }
            this.scrollToBottom()
          }
        } finally {
          this.isSending = false
        }
      }
    },
    beforeUpload() {
      if (!this.selectedDialog || !this.selectedDialog.client_id) {
        this.$message.warning('Выберите диалог перед загрузкой файла')
        return false
      }
      return false
    },
    handleFileChange(file, fileList) {
      this.uploadFiles = fileList
      this.pendingFiles = fileList.map(item => item)
    },
    fileUrl(message) {
      if (message && typeof message.file_url === 'string' && message.file_url) return message.file_url
      const attachments = message && message.attachments
      if (!attachments) return ''
      if (typeof attachments === 'string') return attachments
      if (Array.isArray(attachments) && attachments.length) {
        const firstAttachment = attachments[0]
        if (typeof firstAttachment === 'string') return firstAttachment
        if (firstAttachment && typeof firstAttachment === 'object' && firstAttachment.url) return firstAttachment.url
      }
      if (typeof attachments === 'object' && attachments.url) return attachments.url
      return ''
    },
    isImageUrl(url) {
      return /\.(jpg|jpeg|png|gif|webp)$/i.test(url || '')
    },
    isVideoUrl(url, message) {
      const mime = (message?.mimetype || '').toLowerCase()
      if (mime) return mime.startsWith('video/')
      const extension = (url || '').toLowerCase()
      return extension.endsWith('.mp4') || extension.endsWith('.webm')
    },
    isAudioUrl(url, message) {
      const mime = (message?.mimetype || '').toLowerCase()
      if (mime) return mime.startsWith('audio/')
      const extension = (url || '').toLowerCase()
      return /\.(mp3|m4a|aac|ogg|oga|wav|opus)$/i.test(extension)
    },
    attachmentName(message) {
      const url = this.fileUrl(message) || ''
      try {
        const lastChunk = url.split('/').pop() || ''
        const decoded = decodeURIComponent(lastChunk)
        return decoded || 'файл'
      } catch {
        return 'файл'
      }
    },
    shouldShowContent(message) {
      const text = (message && message.content ? String(message.content) : '').trim()
      if (!text) return false
      const url = this.fileUrl(message)
      if (!url) return true
      try {
        const name = this.attachmentName(message).trim()
        return text !== name
      } catch {
        return true
      }
    }
  },
  mounted() {
    this.seenMessages = new Set()
    this.observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return
        const messageId = entry.target.getAttribute('data-message-id')
        const message = this.sortedMessages.find(item => String(item.id) === String(messageId))
        if (message && !message.read_at && message.sender_type !== 'manager') {
          this.seenMessages.add(message.id)
          if (this.seenFlushTimeout) {
            clearTimeout(this.seenFlushTimeout)
          }
          this.seenFlushTimeout = setTimeout(() => {
            const ids = Array.from(this.seenMessages)
            if (ids.length) {
              this.seenMessages.clear()
              this.$store.dispatch('messengers/markAsReadBatch', {
                message_ids: ids,
                dialog_id: Number(this.selectedDialog.id),
                messenger_type: this.selectedDialog.messenger_type
              })
            }
            this.seenFlushTimeout = null
          }, 500)
        }
      })
    }, { threshold: 0.5 })

    this.$nextTick(() => {
      setTimeout(() => {
        const container = this.$refs.chatContainer
        if (!container || !this.observer) return
        const allElements = container.querySelectorAll('[data-message-id]')
        allElements.forEach(element => {
          this.observer.observe(element)
        })
        this.scrollToBottom()
      }, 50)
    })
  },
  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect()
    }
  }
}
</script>

<style scoped>
.chat-panel {
  --chat-border: rgba(49, 90, 157, 0.95);
  --chat-border-soft: rgba(101, 119, 160, 0.52);
  --chat-deep: #06122c;
  --chat-deep-soft: #0a1838;
  --chat-cyan: #00c3fe;
  --chat-cyan-dim: #02a9de;
  --chat-white: #ffffff;
  --chat-header-bg: linear-gradient(180deg, #214a94 0%, #17386f 100%);
  --chat-composer-bg: linear-gradient(180deg, #214a94 0%, #17386f 100%);
  --chat-surface-bg: linear-gradient(180deg, rgba(11, 26, 59, 0.98) 0%, rgba(7, 18, 43, 0.99) 100%);
  --chat-incoming-bg: linear-gradient(180deg, #4f82d5 0%, #3b68b9 100%);
  --chat-outgoing-bg: linear-gradient(180deg, #4572c4 0%, #315fae 100%);
  --chat-call-bg: linear-gradient(180deg, #4b7cce 0%, #3968bc 100%);
  --chat-pattern: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='180' viewBox='0 0 180 180'%3E%3Cg fill='none' stroke='%23284776' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' opacity='.48'%3E%3Cpath d='M20 24l4 8 9 1-6 6 2 9-8-5-8 5 2-9-6-6 9-1z'/%3E%3Cpath d='M78 18h28a9 9 0 0 1 9 9v14a9 9 0 0 1-9 9H92l-8 9v-9H78a9 9 0 0 1-9-9V27a9 9 0 0 1 9-9z'/%3E%3Cpath d='M140 26l10 19-6 4 2 11-9-6-9 6 2-11-6-4 10-19z'/%3E%3Cpath d='M28 90h28a8 8 0 0 1 8 8v18a8 8 0 0 1-8 8H39l-9 8v-8h-2a8 8 0 0 1-8-8V98a8 8 0 0 1 8-8z'/%3E%3Cpath d='M86 92h30a8 8 0 0 1 8 8v20a8 8 0 0 1-8 8H86a8 8 0 0 1-8-8v-20a8 8 0 0 1 8-8z'/%3E%3Cpath d='M78 98l23 16 23-16'/%3E%3Cpath d='M136 88c0-8 6-14 14-14s14 6 14 14-6 14-14 14-14 6-14 14 6 14 14 14 14-6 14-14'/%3E%3Cpath d='M26 148l6-18 18-6-12 12z'/%3E%3Cpath d='M84 146l4 8 9 1-6 6 2 9-8-5-8 5 2-9-6-6 9-1z'/%3E%3Cpath d='M140 144h18m-9-9v18'/%3E%3C/g%3E%3C/svg%3E");
  min-height: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.chat-panel__empty {
  min-height: 0;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  background: var(--chat-header-bg);
  box-shadow: inset 0 0 0 1px rgba(52, 96, 167, 0.45);
}

.chat-panel__empty-card {
  width: min(420px, calc(100% - 32px));
  padding: 28px 24px;
  text-align: center;
  border-radius: 16px;
  background: rgba(8, 22, 48, 0.76);
  box-shadow: inset 0 0 0 1px rgba(66, 110, 187, 0.32);
}

.chat-panel__empty-image {
  width: 92px;
  height: 92px;
  object-fit: contain;
  opacity: 0.45;
}

.chat-panel__empty-title {
  margin-top: 14px;
  color: #ffffff;
  font-family: "Oswald", sans-serif;
  font-size: 30px;
}

.chat-panel__empty-card p {
  margin: 10px 0 0;
  color: #9ec4ff;
  font-family: "Oswald", sans-serif;
  font-size: 18px;
}

.chat-panel__header {
  min-height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 9px 18px;
  border-radius: 10px;
  background: var(--chat-header-bg);
  box-shadow: inset 0 0 0 1px rgba(52, 96, 167, 0.45);
}

.chat-panel__header-main {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
}

.chat-panel__header-icon,
.chat-panel__header-action {
  width: 56px;
  height: 56px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(7, 18, 44, 0.74);
  box-shadow: inset 0 0 0 1px rgba(44, 88, 155, 0.6);
  color: #00c3fe;
  text-decoration: none;
  border: none;
  padding: 0;
}

.chat-panel__header-icon-image {
  width: 28px;
  height: 28px;
  display: block;
  object-fit: contain;
}

.chat-panel__header-action {
  flex-shrink: 0;
  border-radius: 18px;
}

.chat-panel__header-action-image {
  width: 28px;
  height: 28px;
  display: block;
  object-fit: contain;
}

.chat-panel__header-meta {
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.chat-panel__title {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--chat-white);
  font-family: "Bahnschrift", "DIN Alternate", "Oswald", sans-serif;
  font-size: 20px;
  font-weight: 400;
  line-height: 1;
}

.chat-panel__subtitle {
  margin-top: 4px;
  color: #d8e6ff;
  font-family: "Bahnschrift", "DIN Alternate", "Oswald", sans-serif;
  font-size: 11px;
  font-weight: 300;
  line-height: 1;
}

.chat-panel__messages {
  min-height: 0;
  flex: 1;
  overflow: auto;
  padding: 14px 18px 12px;
  border-radius: 10px;
  background-color: #06122c;
  background-image:
    linear-gradient(180deg, rgba(10, 25, 57, 0.96) 0%, rgba(7, 18, 44, 0.98) 100%),
    var(--chat-pattern);
  background-repeat: no-repeat, repeat;
  background-size: 100% 100%, 180px 180px;
  background-position: 0 0, 0 0;
  box-shadow: inset 0 0 0 1px rgba(52, 96, 167, 0.45);
  scrollbar-width: thin;
  scrollbar-color: rgba(160, 193, 255, 0.24) transparent;
}

.chat-panel__messages::-webkit-scrollbar {
  width: 10px;
}

.chat-panel__messages::-webkit-scrollbar-track {
  background: transparent;
}

.chat-panel__messages::-webkit-scrollbar-thumb {
  border: 3px solid transparent;
  border-radius: 999px;
  background: rgba(160, 193, 255, 0.24);
  background-clip: padding-box;
}

.chat-panel--success .chat-panel__messages {
  background-image:
    linear-gradient(180deg, rgba(19, 61, 56, 0.88) 0%, rgba(8, 29, 43, 0.96) 100%),
    var(--chat-pattern);
}

.chat-panel--overdue .chat-panel__messages {
  background-image:
    linear-gradient(180deg, rgba(75, 24, 36, 0.88) 0%, rgba(20, 12, 32, 0.96) 100%),
    var(--chat-pattern);
}

.chat-group + .chat-group {
  margin-top: 12px;
}

.chat-date-chip {
  width: fit-content;
  margin: 0 auto 18px;
  padding: 4px 14px;
  border-radius: 999px;
  background: rgba(16, 41, 85, 0.82);
  color: #9dc8ff;
  font-family: "Bahnschrift", "DIN Alternate", "Oswald", sans-serif;
  font-size: 12px;
  font-weight: 300;
}

.chat-message-row {
  display: flex;
  margin-bottom: 14px;
}

.chat-message-row--mine {
  justify-content: flex-end;
}

.chat-message-row--other {
  justify-content: flex-start;
}

.chat-message-stack {
  max-width: min(76%, 980px);
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.chat-message-stack--mine .chat-message-item {
  border-top-right-radius: 10px;
  background: var(--chat-outgoing-bg);
}

.chat-message-stack--other .chat-message-item {
  border-top-left-radius: 10px;
  background: var(--chat-incoming-bg);
}

.chat-message-item {
  position: relative;
  min-width: 128px;
  padding: 10px 12px 9px;
  border-radius: 14px;
  box-shadow: inset 0 0 0 1px rgba(101, 119, 160, 0.34), 0 12px 22px rgba(2, 10, 24, 0.12);
}

.chat-message-text {
  color: #ffffff;
  font-family: "Bahnschrift", "DIN Alternate", "Oswald", sans-serif;
  font-size: 14px;
  font-weight: 300;
  line-height: 1.25;
  word-break: break-word;
}

.chat-message-meta {
  margin-top: 4px;
  text-align: right;
  color: #86a8df;
  font-family: "Bahnschrift", "DIN Alternate", "Oswald", sans-serif;
  font-size: 11px;
  font-weight: 300;
}

.chat-attachments {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.chat-message-item--voice,
.chat-message-item--call {
  padding: 9px 12px 8px;
  min-width: 164px;
}

.chat-voice-card {
  display: grid;
  grid-template-columns: 30px minmax(118px, 1fr) auto;
  align-items: center;
  gap: 10px;
  min-height: 28px;
}

.chat-voice-card__play {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.96);
  padding: 0;
  box-shadow: 0 2px 8px rgba(9, 19, 42, 0.16);
}

.chat-voice-card__triangle {
  width: 0;
  height: 0;
  margin-left: 2px;
  border-top: 6px solid transparent;
  border-bottom: 6px solid transparent;
  border-left: 10px solid #4f82d5;
}

.chat-voice-card__wave {
  height: 14px;
  background-position: center;
  background-size: contain;
  background-repeat: no-repeat;
}

.chat-voice-card__duration {
  color: #d8ebff;
  font-family: "Bahnschrift", "DIN Alternate", "Oswald", sans-serif;
  font-size: 11px;
  font-weight: 300;
  line-height: 1;
  white-space: nowrap;
}

.chat-call-card {
  display: grid;
  grid-template-columns: 24px 1fr;
  align-items: center;
  column-gap: 10px;
  row-gap: 2px;
}

.chat-call-card__arrow {
  width: 12px;
  height: 12px;
  display: block;
  object-fit: contain;
  grid-column: 1;
  grid-row: 1 / span 2;
  justify-self: start;
  align-self: start;
  margin-top: 4px;
}

.chat-call-card__phone {
  width: 22px;
  height: 22px;
  display: block;
  object-fit: contain;
  grid-column: 1;
  grid-row: 1 / span 2;
  justify-self: center;
  align-self: center;
}

.chat-call-card__meta {
  min-width: 0;
  grid-column: 2;
  grid-row: 1 / span 2;
}

.chat-call-card__primary {
  color: #ffffff;
  font-family: "Bahnschrift", "DIN Alternate", "Oswald", sans-serif;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.05;
}

.chat-call-card__secondary {
  margin-top: 3px;
  color: #c7daf8;
  font-family: "Bahnschrift", "DIN Alternate", "Oswald", sans-serif;
  font-size: 11px;
  font-weight: 300;
  line-height: 1;
}

.chat-attachment-image,
.chat-attachment-video {
  width: min(220px, 100%);
  max-height: 220px;
  max-width: 100%;
  border-radius: 10px;
  overflow: hidden;
  object-fit: cover;
}

.chat-attachment-audio {
  width: min(240px, 100%);
  max-width: 100%;
}

.chat-attachment-link {
  width: fit-content;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #c5e7ff;
  text-decoration: none;
  font-family: "Oswald", sans-serif;
  font-size: 14px;
}

.chat-panel__composer {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.chat-panel__upload {
  display: none;
}

.chat-panel__files {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.chat-panel__file {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(160px, 220px) auto;
  gap: 10px;
  align-items: center;
  padding: 10px 12px;
  border-radius: 10px;
  background: linear-gradient(180deg, #17366b 0%, #112954 100%);
  box-shadow: inset 0 0 0 1px rgba(52, 96, 167, 0.45);
}

.chat-panel__file-name,
.chat-panel__file-ready {
  min-width: 0;
  color: #d7e8ff;
  font-family: "Oswald", sans-serif;
  font-size: 15px;
}

.chat-panel__file-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chat-panel__file-ready {
  color: #8de5a5;
}

.chat-panel__file-remove {
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 50%;
  background: #0c224b;
  box-shadow: inset 0 0 0 1px rgba(52, 96, 167, 0.45);
  color: #ffffff;
}

.chat-panel__composer-shell {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: stretch;
  padding: 12px 18px 12px 26px;
  border-radius: 12px;
  background: var(--chat-composer-bg);
  box-shadow: inset 0 0 0 1px rgba(52, 96, 167, 0.45);
}

.chat-panel__composer-shell::before {
  content: '';
  width: 3px;
  border-radius: 999px;
  background: #00c3fe;
  position: absolute;
  left: 18px;
  top: 16px;
  bottom: 16px;
  opacity: 1;
}

.chat-panel__composer-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.chat-panel__icon-btn {
  width: 44px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #00c3fe;
  padding: 0;
}

.chat-panel__composer-icon-image {
  display: block;
  object-fit: contain;
}

.chat-panel__icon-btn:first-child .chat-panel__composer-icon-image {
  width: 28px;
  height: 32px;
}

.chat-panel__icon-btn--primary .chat-panel__composer-icon-image {
  width: 22px;
  height: 36px;
}

.chat-panel__icon-btn--primary {
  background: transparent;
}

.chat-panel__icon-btn:disabled {
  opacity: 0.4;
}

.chat-editor {
  min-width: 0;
}

.chat-editor :deep(.ql-toolbar) {
  display: none;
}

.chat-editor :deep(.ql-container) {
  border: none !important;
  font-family: "Bahnschrift", "DIN Alternate", "Oswald", sans-serif;
  background: transparent;
}

.chat-editor :deep(.ql-editor) {
  min-height: 44px;
  padding: 2px 0;
  color: #00c3fe;
  font-size: 18px;
  line-height: 1.35;
}

.chat-editor :deep(.ql-editor.ql-blank::before) {
  left: 0;
  right: auto;
  color: #00c3fe;
  font-style: normal;
}

.chat-editor :deep(.ql-editor p) {
  margin: 0;
}

.chat-message-text :deep(p) {
  margin: 0;
}

.chat-message-text :deep(a) {
  color: #d8f1ff;
}

.chat-panel__file-progress :deep(.el-progress-bar__outer) {
  background: rgba(255, 255, 255, 0.12) !important;
}

.chat-panel__header-icon:hover,
.chat-panel__header-action:hover,
.chat-panel__icon-btn:hover {
  box-shadow: inset 0 0 0 1px rgba(0, 195, 254, 0.82);
}

@media (max-width: 1180px) {
  .chat-panel__title {
    font-size: 18px;
  }

  .chat-message-stack {
    max-width: 84%;
  }
}

@media (max-width: 900px) {
  .chat-panel__header {
    padding: 10px 14px;
  }

  .chat-panel__title {
    font-size: 18px;
  }

  .chat-panel__messages {
    padding: 12px 14px 10px;
  }

  .chat-panel__file {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .chat-panel__header {
    gap: 10px;
  }

  .chat-panel__header-main {
    justify-content: flex-start;
    gap: 10px;
  }

  .chat-panel__header-meta {
    align-items: flex-start;
  }

  .chat-panel__header-icon,
  .chat-panel__header-action {
    width: 44px;
    height: 44px;
  }

  .chat-panel__header-icon-image,
  .chat-panel__header-action-image {
    width: 22px;
    height: 22px;
  }

  .chat-panel__title {
    font-size: 18px;
  }

  .chat-panel__subtitle {
    font-size: 14px;
  }

  .chat-message-stack {
    max-width: 94%;
  }

  .chat-message-item {
    min-width: 0;
  }

  .chat-message-text {
    font-size: 13px;
  }

  .chat-panel__composer-shell {
    gap: 10px;
    padding: 10px 14px 10px 22px;
  }

  .chat-panel__composer-actions {
    justify-content: flex-end;
  }

  .chat-panel__composer-shell::before {
    left: 14px;
    top: 14px;
    bottom: 14px;
  }

  .chat-voice-card {
    grid-template-columns: 28px minmax(88px, 1fr) auto;
    gap: 8px;
  }

  .chat-voice-card__wave {
    height: 12px;
  }

  .chat-call-card__primary {
    font-size: 12px;
  }

  .chat-call-card__secondary,
  .chat-voice-card__duration,
  .chat-message-meta {
    font-size: 10px;
  }
}

@media (max-width: 420px) {
  .chat-panel__messages {
    padding: 10px 12px 8px;
  }

  .chat-panel__header {
    padding: 8px 10px;
  }

  .chat-panel__composer-shell {
    padding: 8px 12px 8px 20px;
  }

  .chat-panel__title {
    font-size: 16px;
  }

  .chat-panel__subtitle {
    font-size: 10px;
  }

  .chat-panel__icon-btn {
    width: 38px;
    height: 38px;
  }

  .chat-panel__icon-btn:first-child .chat-panel__composer-icon-image {
    width: 24px;
    height: 28px;
  }

  .chat-panel__icon-btn--primary .chat-panel__composer-icon-image {
    width: 18px;
    height: 30px;
  }

  .chat-message-stack {
    max-width: 100%;
  }
}
</style>
